import type { RefObject } from 'react'
import { gsap, useGSAP, registerMotion, NO_PREF } from './gsap'

/**
 * A client navigation: start the new page at the top, then fade it up
 * (250 ms).
 *
 * `animateIn` comes from SiteRoutes, which derives it during render from the
 * previous pathname it saw: false on the server and on the first client render
 * (hydration never animates), true only when the pathname really changed.
 * No module-level "first mount" flag — React StrictMode runs this hook twice
 * on mount in dev, and a flag would let the second run animate the initial
 * load.
 *
 * Scroll first: the new page mounts at the previous page's scroll offset, and
 * SiteRoutes scrolls to the top in a passive effect — after every layout
 * effect, so after the hooks that run next (this is PageMotion's first hook)
 * have created their ScrollTriggers. A trigger evaluates the scroll as it is
 * created, so at the old offset every `once` reveal, counter and dot pop above
 * it would fire off-screen and never be seen. `instant`, because the global
 * `html { scroll-behavior: smooth }` (index.css) turns 'auto' into a smooth
 * scroll that would still be under way; SiteRoutes' own scroll then finds the
 * page already at the top. Motion preference does not matter here.
 *
 * Opacity, not autoAlpha: SiteRoutes moves focus to the new <h1> right after
 * this mounts, and an element under `visibility: hidden` cannot take focus.
 *
 * Reduced motion: no fade.
 */
export function useRouteTransition(scope: RefObject<HTMLElement | null>, animateIn: boolean) {
  useGSAP(
    () => {
      registerMotion()
      const el = scope.current
      if (!animateIn || !el) return
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      const mm = gsap.matchMedia()
      mm.add(NO_PREF, () => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 8 },
          { opacity: 1, y: 0, duration: 0.25, ease: 'power1.out', clearProps: 'all' },
        )
      })
    },
    { scope },
  )
}
